import { useMemo, useState } from 'react';
import { calculateNetProceeds } from '../lib/sellerCalc';
import { formatYen, splitYen } from '../lib/format';

interface Props {
  /** 推定価格 (円). Used as the default 売却予定価格. */
  predictedPrice: number;
}

const CURRENT_YEAR = new Date().getFullYear();

export default function NetProceedsCalc({ predictedPrice }: Props) {
  const [salePriceStr, setSalePriceStr] = useState<string>(String(predictedPrice));
  const [acquisitionPriceStr, setAcquisitionPriceStr] = useState<string>('');
  const [acquisitionYearStr, setAcquisitionYearStr] = useState<string>('');
  const [wasResidence, setWasResidence] = useState<boolean>(true);
  const [loanRemainingStr, setLoanRemainingStr] = useState<string>('');

  const result = useMemo(() => {
    const salePrice = Number(salePriceStr) || 0;
    if (salePrice <= 0) return null;
    return calculateNetProceeds({
      salePrice,
      acquisitionPrice: acquisitionPriceStr ? Number(acquisitionPriceStr) : undefined,
      acquisitionYear: acquisitionYearStr ? Number(acquisitionYearStr) : undefined,
      wasResidence,
      saleYear: CURRENT_YEAR,
      loanRemaining: loanRemainingStr ? Number(loanRemainingStr) : undefined,
    });
  }, [salePriceStr, acquisitionPriceStr, acquisitionYearStr, wasResidence, loanRemainingStr]);

  const net = result ? splitYen(result.netProceeds) : null;

  return (
    <div>
      <div className="grid grid-cols-2 gap-3">
        <label className="col-span-2 block">
          <span className="field-label">売却予定価格 <span className="text-ink-3">円</span></span>
          <input
            type="number"
            min={0}
            value={salePriceStr}
            onChange={(e) => setSalePriceStr(e.target.value)}
            className="field"
          />
        </label>
        <label className="block">
          <span className="field-label">取得価格 <span className="text-ink-3">円・任意</span></span>
          <input
            type="number"
            min={0}
            value={acquisitionPriceStr}
            onChange={(e) => setAcquisitionPriceStr(e.target.value)}
            placeholder="空欄なら売却額の5%で概算"
            className="field"
          />
        </label>
        <label className="block">
          <span className="field-label">取得年 <span className="text-ink-3">任意</span></span>
          <input
            type="number"
            min={1950}
            max={CURRENT_YEAR}
            value={acquisitionYearStr}
            onChange={(e) => setAcquisitionYearStr(e.target.value)}
            placeholder="2015"
            className="field"
          />
        </label>
        <label className="col-span-2 block">
          <span className="field-label">ローン残債 <span className="text-ink-3">円・任意</span></span>
          <input
            type="number"
            min={0}
            value={loanRemainingStr}
            onChange={(e) => setLoanRemainingStr(e.target.value)}
            placeholder="18000000"
            className="field"
          />
        </label>
        <label className="col-span-2 flex items-start gap-2 text-[13px] leading-snug text-ink-2">
          <input
            type="checkbox"
            checked={wasResidence}
            onChange={(e) => setWasResidence(e.target.checked)}
            className="mt-0.5 accent-primary"
          />
          <span>自分が住んでいた家だった（3,000 万円の特別控除を適用する）</span>
        </label>
      </div>

      {result && net && (
        <>
          <dl className="mt-5 space-y-1.5 text-[13px]">
            <Row label="売却価格" value={result.salePrice} />
            <Row label="仲介手数料" value={-result.brokerageFee} />
            <Row label="印紙税" value={-result.stampDuty} />
            {result.registrationFee > 0 && (
              <Row label="抵当権の抹消" value={-result.registrationFee} />
            )}
            <Row label="譲渡所得税" value={-result.capitalGainsTax} />
            {result.loanRemaining > 0 && (
              <Row label="ローン残債の返済" value={-result.loanRemaining} />
            )}
          </dl>

          <div className="mt-4 flex items-baseline justify-between rounded-lg bg-tint-warm px-3.5 py-3">
            <span className="text-[13px] font-bold text-ink">手元に残る額</span>
            <span className="flex items-baseline gap-1">
              <span className="fig text-[26px] font-bold text-accent">{net.value}</span>
              <span className="text-[13px] font-bold tracking-wide text-accent">{net.unit}</span>
            </span>
          </div>

          <ul className="mt-3 space-y-1 text-[11px] leading-jp text-ink-3">
            {result.notes.map((n, i) => (
              <li key={i}>{n}</li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: number }) {
  const negative = value < 0;
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-ink-3">{label}</dt>
      <dd className={`fig ${negative ? 'text-debit' : 'text-ink-2'}`}>
        {negative ? `−${formatYen(-value)}` : formatYen(value)}
      </dd>
    </div>
  );
}
